import React, { useEffect, useState } from 'react'
import RenderPrice from '../common/RenderPrice'
import TooltipClick from '../common/TooltipClick'
import BasePriceDetail from './BasePriceDetail'
import { isEmpty } from '../../utils'
import BaseTaxesAndFeesDetail from './BaseTaxesAndFeesDetail'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { setPromoCode } from '../../redux/slices/property'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

interface Props {
  data: any
  currency: string
  nights: number
}

const PriceBreakDown: React.FC<Props> = ({ data, currency, nights }) => {
  const dispatch = useAppDispatch()
  const { cryptoPayment } = useAppSelector((state) => state.common)
  const [rate, setRate] = useState<number>(1)
  const [discountData, setDiscountData] = useState<any[]>([])
  const [feeData, setFeeData] = useState<any[]>([])
  const [taxData, setTaxData] = useState<any[]>([])
  const [addonsData, setAddonsData] = useState<any[]>([])
  const [totalFee, setTotalFee] = useState<number>(0)
  const [totalTax, setTotalTax] = useState<number>(0)
  const isCryptoPayment = !isEmpty(cryptoPayment)

  useEffect(() => {
    if (data.exchangeRate && currency) {
      const { exchangeRate } = data
      const rates = exchangeRate.rates
      const rate = rates[currency.toUpperCase()]
      setRate(rate)

      const _discountData: Array<any> = isCryptoPayment ?
        (Array.isArray(data?.priceDetail?.discount) &&
          data.priceDetail.discount.filter(
            (discount: any) => discount.isIncludedInTotalPrice && discount.name !== 'creditCardProcessingFee'
          )) ||
        []
        : (Array.isArray(data?.priceDetail?.discount) &&
          data.priceDetail.discount.filter(
            (discount: any) =>
              discount.isIncludedInTotalPrice &&
              discount.name !== 'creditCardProcessingFee' &&
              discount.name !== 'creditCardFeesAbsorbedByHost'
          )) ||
        []

      // const discountCreditCardProcessingFee: any =
      //   Array.isArray(data?.priceDetail?.discount) &&
      //   data.priceDetail.discount.find((discount: any) => discount.name === 'creditCardProcessingFee')

      const _feeData: Array<any> =
        (Array.isArray(data?.priceDetail?.fee) &&
          data.priceDetail.fee.filter((fee: any) => fee.isIncludedInTotalPrice)) ||
        []
      const _taxData: Array<any> =
        (Array.isArray(data?.priceDetail?.tax) &&
          data.priceDetail.tax.filter((tax: any) => tax.isIncludedInTotalPrice)) ||
        []
      const _addonsData: Array<any> =
        (Array.isArray(data?.priceDetail?.addons) &&
          data.priceDetail.addons.filter((fee: any) => fee.isIncludedInTotalPrice)) ||
        []
      const _totalFee = _feeData.reduce((accum, item: any) => accum + Number(item.total || item.value), 0)
      const _totalTax = _taxData.reduce((accum, item) => accum + Number(item.total || item.value), 0)
      // const priceCreditCardProcessingFeeWaived = discountCreditCardProcessingFee
      //   ? discountCreditCardProcessingFee
      //     ? Number(discountCreditCardProcessingFee.value * rate)
      //     : 0
      //   : Number(data?.platformFee?.[currency] || 0)
      setDiscountData(_discountData)
      setFeeData(_feeData)
      setTaxData(_taxData)
      setAddonsData(_addonsData)
      setTotalFee(_totalFee)
      setTotalTax(_totalTax)
    }
  }, [data, currency])

  const onClickRemovePromoCode = () => {
    dispatch(setPromoCode(''))
  }
  const discountCreditCardProcessingFee: any =
    Array.isArray(data?.priceDetail?.discount) &&
    data.priceDetail.discount.find((discount: any) => discount.name === 'creditCardProcessingFee')
  const priceCreditCardProcessingFeeWaived = discountCreditCardProcessingFee
    ? discountCreditCardProcessingFee
      ? Number(discountCreditCardProcessingFee.value * rate)
      : 0
    : Number(data?.platformFee?.[currency] || 0)
  return (
    <div className={'dtravel-inter-400 dtravel-text-14-18 bg-grayscale-100 rounded-[12px] p-[16px]'}>
      <div className={'dtravel-flex-between mb-[8px]'}>
        <TooltipClick
          title={<BasePriceDetail priceInfo={data} currency={currency} />}
          header="Base Price Details"
          type="basePrice"
        >
          <p className="dtravel-text-14-18 dtravel-inter-400 text-neutral-900">
            <RenderPrice
              price={data.basePricePerNight ? Number(data.basePricePerNight * rate) : 0}
              currency={currency}
            />{' '}
            x {nights || 0} {nights > 1 ? 'nights' : 'night'}
          </p>
        </TooltipClick>
        <span className="dtravel-text-14-18 dtravel-grayscale-900 dtravel-inter-500">
          <RenderPrice price={data.price * rate} currency={currency} />
        </span>
      </div>

      {/* ---DISCOUNT--- */}
      {!isEmpty(discountData) && (
        <div className={'dtravel-flex-between mb-[8px]'}>
          <TooltipClick
            title={<BaseTaxesAndFeesDetail priceInfo={data} currency={currency} discountData={discountData} />}
            header="Discounts"
            type="discounts"
          >
            <p className="dtravel-text-14-18 dtravel-inter-400 text-neutral-900">Discounts</p>
          </TooltipClick>

          <span className="dtravel-text-14-18 dtravel-inter-400 text-neutral-900" style={{ color: '#3F753C' }}>
            <RenderPrice price={Number(data.priceDetail.totalDiscount.value * rate)} currency={currency} />
          </span>
        </div>
      )}

      {/* ---FEE--- */}
      {!isEmpty(feeData) && (
        <div className={'dtravel-flex-between'} style={{ marginBottom: 8 }}>
          <TooltipClick
            title={<BaseTaxesAndFeesDetail priceInfo={data} currency={currency} feeData={feeData} total={totalFee} />}
            header="Fees Details"
            type="fees"
          >
            <p className="dtravel-text-14-18 dtravel-inter-400 text-neutral-900">Fees</p>
          </TooltipClick>
          <span className="dtravel-text-14-18 text-neutral-900 dtravel-inter-400">
            <RenderPrice price={Number(totalFee || 0) * rate} currency={currency} />
            {/* <RenderPrice price={Number(data.priceDetail.taxAndFee.value || 0) * rate} currency={currencyDisplay} /> */}
          </span>
        </div>
      )}

      {/* ---Add-ons--- */}
      {!isEmpty(addonsData) && (
        <div className={'dtravel-flex-between mb-[8px]'}>
          <TooltipClick
            title={<BaseTaxesAndFeesDetail priceInfo={data} currency={currency} addonsData={addonsData} />}
            header="Additional Services"
            type="additionalServices"
          >
            <p className="dtravel-text-14-18 dtravel-inter-400 text-neutral-900">Additional Services</p>
          </TooltipClick>
          <span className="dtravel-text-14-18 dtravel-neutral-900 dtravel-inter-400">
            <RenderPrice
              // price={Number(data?.priceDetail?.totalAddOns?.convertedValue?.[currencyDisplay] || 0)}
              price={Number(data?.priceDetail?.totalAddOns?.value || 0) * rate}
              currency={currency}
            />
          </span>
        </div>
      )}

      {/* ---Taxes--- */}
      {!isEmpty(taxData) && (
        <div className={'dtravel-flex-between mb-[8px]'}>
          <TooltipClick
            title={<BaseTaxesAndFeesDetail priceInfo={data} currency={currency} taxData={taxData} total={totalTax} />}
            header="Taxes Details"
            type="taxes"
          >
            <p className="dtravel-text-14-18 dtravel-inter-400 text-neutral-900">Taxes</p>
          </TooltipClick>
          <span className="dtravel-text-14-18 text-neutral-900 dtravel-inter-400">
            <RenderPrice price={Number(totalTax || 0) * rate} currency={currency} />
            {/* <RenderPrice price={Number(data.priceDetail.taxAndFee.value || 0) * rate} currency={currencyDisplay} /> */}
          </span>
        </div>
      )}
      {/* ---Card processing fee waived--- */}
      {(discountCreditCardProcessingFee || isCryptoPayment) && priceCreditCardProcessingFeeWaived !== 0 && (
        <div
          className={`flex justify-between mb-2 text-14-18 text-grayscale-900 font-inter-400 ${isCryptoPayment ? '' : 'hidden'}`}
        >
          <div className="flex items-start dtravel-text-14-18 dtravel-inter-400 text-neutral-900">
            Card processing fee waived
            <TooltipClick
              placement="top"
              title={
                <span className="font-inter-400 text-12-16 text-grayscale-4">
                  This fee is discounted when you book with crypto.
                </span>
              }
              arrow
              type="feeDiscounted"
            >
              <HelpOutlineIcon style={{ width: 16, fontSize: 14, marginRight: 16, marginLeft: 2, marginTop: 2 }} />
            </TooltipClick>
          </div>
          <span className="text-forest-700 text-14-18 font-inter-500 whitespace-nowrap">
            <RenderPrice
              price={priceCreditCardProcessingFeeWaived}
              currency={cryptoPayment}
            />
          </span>
        </div>
      )}
      {/* Promotion code */}
      <>
        {!isEmpty(data?.priceDetail?.promotionDiscount) && (
          <>
            {data?.priceDetail?.promotionDiscount?.map((el: any, idx: number) => {
              return (
                <div
                  className={`flex items-center justify-between mb-6 text-14-18 text-grayscale-900 font-inter-400`}
                  key={idx}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-inter-400 dtravel-text-14-18 text-neutral-900">Promocode</span>
                    <div className="flex items-center gap-2 bg-forest-50 rounded-[24px] px-[8px] py-[2px]">
                      <span className="dtravel-text-12-16 font-inter-600 text-forest-700">{el?.name}</span>
                      <button className={'w-[12px] h-[12px]'} onClick={onClickRemovePromoCode}>
                        <svg xmlns="http://www.w3.org/2000/svg" width={8} height={8} viewBox="0 0 8 8" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0.184985 0.184985C0.431631 -0.0616616 0.831524 -0.0616616 1.07817 0.184985L7.81498 6.92179C8.06162 7.16844 8.06162 7.56833 7.81498 7.81498C7.56833 8.06162 7.16844 8.06162 6.92179 7.81498L0.184985 1.07817C-0.0616616 0.831524 -0.0616616 0.431631 0.184985 0.184985Z"
                            fill="#3F753C"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7.81501 0.184985C8.06166 0.431631 8.06166 0.831524 7.81501 1.07817L1.07817 7.81501C0.831524 8.06166 0.431631 8.06166 0.184985 7.81501C-0.0616616 7.56837 -0.0616616 7.16848 0.184985 6.92183L6.92183 0.184985C7.16848 -0.0616616 7.56837 -0.0616616 7.81501 0.184985Z"
                            fill="#3F753C"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <span className="text-forest-700 text-14-18 font-inter-500">
                    <RenderPrice price={el?.value * rate} currency={currency} />
                  </span>
                </div>
              )
            })}
          </>
        )}
      </>

      <div className={'h-[1px] bg-[#00000026] my-[16px]'} />
      <div className={'dtravel-flex-between dtravel-inter-500 text-neutral-900 dtravel-text-14-18'}>
        <span className={''}>Total: </span>
        <span>
          <RenderPrice price={data?.finalPrice?.[currency]} currency={currency} />
        </span>
      </div>
    </div>
  )
}

export default PriceBreakDown
